/**
 * useOrderManagementBuildRequests — list viewmodel for
 * /order-management/build-requests (operator review surface).
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination (LOCKED)". Composes
 * the shared usePaginatedList hook so paging + loading + error all
 * follow the established pattern.
 *
 * One row per build_request — the header carries customer name /
 * email / phone, the assigned organisation, status, item_count, the
 * org's max_allowed_count (when set), the resolved subdomain (when
 * built), and timestamps. The detail/edit modal loads via the
 * separate useBuildRequestDetail hook so list paging stays cheap.
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

export interface BuildRequestListDto {
  readonly id: string
  readonly customer_id: string
  readonly customer_first_name: string | null
  readonly customer_last_name: string | null
  readonly customer_email: string
  readonly customer_phone: string | null
  readonly organisation_id: string
  readonly organisation_name: string
  readonly status: 'requested' | 'confirmed' | 'built' | 'cancelled' | string
  readonly item_count: number
  readonly max_allowed_count: number | null
  /** Per-tenant cart-value cap (paisa) joined from
   *  organisations.max_cart_value_paisa. NULL until the operator sets
   *  it during build-request review. Migration 0111. */
  readonly max_cart_value_paisa: number | null
  readonly subdomain: string | null
  readonly requested_at: string
  readonly built_at: string | null
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: BuildRequestListDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

export function useOrderManagementBuildRequests(): {
  readonly requests: ReadonlyArray<BuildRequestListDto>
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  /** Inline cancel — flips status='cancelled' on a single request.
   *  Surfaced as a per-row Action button on the list table (Kiran
   *  2026-06-08) so the operator doesn't have to open the modal for a
   *  one-click reject. Caller is expected to confirm beforehand. */
  readonly cancel: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
  /** Inline publish — provisions the per-tenant subdomain at Cloudflare
   *  and flips status='built' (Kiran 2026-06-08: moved out of the modal,
   *  enabled only when the row is 'confirmed'). The backend still
   *  enforces all the preconditions (org assigned, max_allowed_count
   *  set, slug doesn't collide); failures bubble through as a UI
   *  message. Caller confirms beforehand. */
  readonly publish: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
  /** Re-attach a built row's CF custom domain when the binding is
   *  missing (silent PUT failure, accidental detach). Idempotent;
   *  D1 state stays as-is. Kiran 2026-06-08 (RidgeGap incident). */
  readonly reattach: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
} {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<BuildRequestListDto>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.ORDERMANAGEMENTBUILDREQUESTS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total }
      },
      [authedFetch]
    )
  })

  const cancel = useCallback(
    async (id: string): Promise<{ ok: true } | { ok: false; message: string }> => {
      try {
        const res = await authedFetch(
          fillPath(APIROUTES.ORDERMANAGEMENTBUILDREQUESTITEM, { id }),
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'cancelled' })
          }
        )
        if (!res.ok) return { ok: false, message: (await parseApiError(res)).message }
        paged.reload()
        return { ok: true }
      } catch (e) {
        return { ok: false, message: e instanceof Error ? e.message : 'Cancel failed' }
      }
    },
    [authedFetch, paged]
  )

  const publish = useCallback(
    async (id: string): Promise<{ ok: true } | { ok: false; message: string }> => {
      try {
        const res = await authedFetch(
          fillPath(APIROUTES.ORDERMANAGEMENTBUILDREQUESTBUILD, { id }),
          { method: 'POST' }
        )
        if (!res.ok) return { ok: false, message: (await parseApiError(res)).message }
        paged.reload()
        return { ok: true }
      } catch (e) {
        return { ok: false, message: e instanceof Error ? e.message : 'Publish failed' }
      }
    },
    [authedFetch, paged]
  )

  const reattach = useCallback(
    async (id: string): Promise<{ ok: true } | { ok: false; message: string }> => {
      try {
        const res = await authedFetch(
          fillPath(APIROUTES.ORDERMANAGEMENTBUILDREQUESTREATTACH, { id }),
          { method: 'POST' }
        )
        if (!res.ok) return { ok: false, message: (await parseApiError(res)).message }
        paged.reload()
        return { ok: true }
      } catch (e) {
        return { ok: false, message: e instanceof Error ? e.message : 'Re-attach failed' }
      }
    },
    [authedFetch, paged]
  )

  return {
    requests: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    cancel,
    publish,
    reattach
  }
}
